/**
 * LE REPLI TYPOGRAPHIQUE — la promesse « jamais de code source à un élève ».
 *
 * ═══ Pourquoi il existe ═══
 *
 * `analyserTexteLatex` est volontairement intransigeant : une commande hors
 * de sa liste fermée fait retomber le segment ENTIER en prose, BRUTE. C'est
 * la bonne décision pour le DESSIN — mieux vaut ne rien affirmer que d'
 * affirmer autre chose. Mais la prose brute, c'est la SOURCE, et le 12
 * septembre le fondateur l'a lue sur son iPad, dans une copie corrigée :
 *
 *     « Tu as tenté l'appariement; corrige avec \(1\to a\), \(2\to c\). »
 *     « y=2x+1\quad\Rightarrow\quad m=2 »
 *     « \(m_1=m_2\Rightarrow\text{sécantes}\) »
 *
 * Élargir la grammaire (`latex.ts`) règle ces cas-là. Elle ne règle pas le
 * cas SUIVANT : un jour, un modèle écrira une commande que personne n'avait
 * prévue, et l'élève relira du code. Ce fichier est la borne basse —
 * après quoi il n'y a plus rien.
 *
 * ═══ Ce que c'est, et ce que ce n'est pas ═══
 *
 * Ce n'est PAS un second analyseur, et surtout pas une seconde vérité : il ne
 * dessine rien, ne structure rien, n'affirme rien sur le sens. Il rend une
 * chaîne LISIBLE — les délimiteurs retirés, les commandes connues remplacées
 * par leur signe, le reste débarrassé de ses barres obliques et de ses
 * accolades. `\frac{1}{2}` devient « 1/2 » : moins bien qu'une fraction
 * empilée, infiniment mieux que « \frac{1}{2} ».
 *
 * C'est une SÉCURITÉ, pas un rendu. Quand il se déclenche dans une correction,
 * le serveur le JOURNALISE (`packages/api/src/modules/copies/latex-hors-contrat.ts`) :
 * une requête qui produit du hors-contrat doit se voir, pas se rattraper en
 * silence.
 *
 * ═══ Deux causes, un seul repli ═══
 *
 * Les captures du fondateur en montrent deux, et il les traite toutes les
 * deux, parce qu'elles se lisent pareil pour un élève :
 *
 *  1. une commande REFUSÉE dans un `\( … \)` — le segment revient en prose
 *     avec ses délimiteurs ;
 *  2. des mathématiques écrites SANS délimiteurs — « 2\ne -5 », « \text{…} »
 *     nus au milieu d'une phrase. `analyserTexteLatex` n'y voit aucun
 *     segment, et ne peut rien en faire : hors de `\( … \)`, tout est prose.
 */
import { enExposantUnicode, enIndiceUnicode } from './unicode.js';
/**
 * Les commandes rendues par leur SIGNE. La table reprend celle de
 * `latex.ts` — c'est voulu : le repli doit rendre le même signe que le
 * dessin, sans quoi la même expression se lirait de deux façons selon
 * qu'elle a été acceptée ou non.
 *
 * Elle va plus loin sur un point : elle nomme aussi des commandes que la
 * grammaire REFUSE (`\alpha` hors liste, `\sum`, `\forall`…). Un signe juste
 * vaut mieux qu'un mot nu, même quand le dessin, lui, a eu raison de refuser.
 */
/** Les noms d'opérateur de la grammaire (`latex.ts`) : `\log`, `\ln`, `\sin`, `\cos`, `\tan`. */
const NOMS_OPERATEUR = new Set([
    'log',
    'ln',
    'sin',
    'cos',
    'tan',
    'sec',
    'csc',
    'cot',
]);
/** Le nom ÉCRIT, quand il diffère de la commande (tranche T45, comme `latex.ts`). */
const NOM_ECRIT = { csc: 'cosec', cot: 'cotan' };
const SIGNES = {
    // Relations
    neq: '≠',
    ne: '≠',
    leq: '≤',
    le: '≤',
    geq: '≥',
    ge: '≥',
    approx: '≈',
    equiv: '≡',
    sim: '∼',
    propto: '∝',
    // Flèches
    to: '→',
    rightarrow: '→',
    leftarrow: '←',
    Rightarrow: '⇒',
    Leftarrow: '⇐',
    Leftrightarrow: '⇔',
    leftrightarrow: '↔',
    mapsto: '↦',
    // Opérations
    cdot: '·',
    times: '×',
    div: '÷',
    pm: '±',
    mp: '∓',
    // Géométrie et ensembles
    angle: '∠',
    triangle: '△',
    parallel: '∥',
    perp: '⊥',
    // La norme (tranche T43) : `\lVert \vec{u} \rVert` → « ‖u‖ »
    lVert: '‖',
    rVert: '‖',
    circ: '°',
    in: '∈',
    notin: '∉',
    cup: '∪',
    cap: '∩',
    subset: '⊂',
    emptyset: '∅',
    // Symboles
    pi: 'π',
    infty: '∞',
    percent: '%',
    Delta: 'Δ',
    delta: 'δ',
    alpha: 'α',
    beta: 'β',
    gamma: 'γ',
    theta: 'θ',
    lambda: 'λ',
    mu: 'μ',
    sigma: 'σ',
    omega: 'ω',
    sum: '∑',
    prod: '∏',
    int: '∫',
    // Ensembles de nombres, quand `\mathbb` a déjà été dépouillé
    ldots: '…',
    dots: '…',
    cdots: '…',
};
/** Les ensembles de nombres de `\mathbb` — la même liste fermée que la grammaire (`latex.ts`). */
const ENSEMBLES_DE_NOMBRES = {
    N: 'ℕ',
    Z: 'ℤ',
    Q: 'ℚ',
    R: 'ℝ',
    C: 'ℂ',
};
/** Les commandes d'ESPACEMENT : une espace, jamais un glyphe. */
const ESPACEMENTS = new Set([
    'quad',
    'qquad',
    'thinspace',
    'medspace',
    'thickspace',
    'hspace',
    'enspace',
]);
/** Les commandes qui DISPARAISSENT en laissant leur contenu : `\text{cm}` → « cm ». */
const TRANSPARENTES = new Set([
    'text',
    'mathrm',
    'mathbb',
    'mathbf',
    'textbf',
    'textit',
    'mathit',
    'operatorname',
    'overline',
    'underline',
    'vec',
    'bar',
    'hat',
    'left',
    'right',
]);
/** Un caractère d'échappement LaTeX : `\%` est un pour cent, pas une commande. */
const ECHAPPES = {
    '%': '%',
    $: '$',
    '&': '&',
    '#': '#',
    _: '_',
    '{': '{',
    '}': '}',
    // La norme (tranche T43) : `\|\vec{u}\|` → « ‖u‖ »
    '|': '‖',
};
const estLettre = (c) => /^[A-Za-z]$/.test(c);
/** Les environnements de matrice (tranche T41) et leurs délimiteurs. */
const PAIRES_MATRICE = { pmatrix: '()', bmatrix: '[]' };
/**
 * `true` si le texte porte encore de la SOURCE — une commande, un délimiteur
 * ou un script à accolades. C'est la condition du repli, et elle est étroite
 * à dessein : une phrase française ordinaire ne la déclenche jamais, donc un
 * texte sain traverse sans être touché d'un caractère.
 */
export function contientLatexBrut(texte) {
    return /\\[A-Za-z]|\\[([{%,;:]|\\[)\]}]|[\^_]\{/.test(texte);
}
/**
 * Lit le groupe `{ … }` qui commence à `i` (qui doit être sur `{`), et rend
 * son contenu BRUT avec la position d'après. Les accolades s'équilibrent :
 * `\frac{\frac{1}{2}}{3}` se lit bien en deux arguments.
 */
function groupe(texte, i) {
    let profondeur = 0;
    for (let j = i; j < texte.length; j += 1) {
        const c = texte.charAt(j);
        if (c === '\\') {
            j += 1;
            continue;
        }
        if (c === '{')
            profondeur += 1;
        else if (c === '}') {
            profondeur -= 1;
            if (profondeur === 0)
                return { contenu: texte.slice(i + 1, j), suite: j + 1 };
        }
    }
    // Accolade jamais fermée (la troncature d'une sortie de modèle) : tout le
    // reste est le contenu. On ne rend pas une accolade orpheline à l'élève.
    return { contenu: texte.slice(i + 1), suite: texte.length };
}
/** Saute les blancs à partir de `i`. */
function apresBlancs(texte, i) {
    let j = i;
    while (j < texte.length && /\s/.test(texte.charAt(j)))
        j += 1;
    return j;
}
/**
 * L'argument d'une commande : un groupe `{ … }`, ou le seul caractère qui
 * suit (`\frac12`, la règle de LaTeX). Rend `null` s'il n'y a rien.
 */
function argument(texte, i) {
    const k = apresBlancs(texte, i);
    if (k >= texte.length)
        return null;
    if (texte.charAt(k) === '{') {
        const g = groupe(texte, k);
        return { contenu: g.contenu, suite: g.suite };
    }
    if (texte.charAt(k) === '\\')
        return null;
    return { contenu: texte.charAt(k), suite: k + 1 };
}
/**
 * L'argument d'un SCRIPT (`^` ou `_`) : un groupe `{ … }`, ou le seul
 * caractère qui suit s'il peut vraiment être un exposant ou un indice.
 *
 * La restriction n'est pas cosmétique. La banque écrit des trous à remplir —
 * « \(…=6x(\) ___ \(+\) ___ » — et la règle permissive de LaTeX y lisait
 * `_` suivi de `_` comme un indice : « ___ » devenait « _() ». Un texte à
 * trous doit garder ses trous.
 */
function argumentScript(texte, i) {
    const k = apresBlancs(texte, i);
    if (k >= texte.length)
        return null;
    if (texte.charAt(k) === '{') {
        const g = groupe(texte, k);
        return { contenu: g.contenu, suite: g.suite };
    }
    if (!/[0-9A-Za-z+\-−=()]/.test(texte.charAt(k)))
        return null;
    return { contenu: texte.charAt(k), suite: k + 1 };
}
/** Le nom de la commande qui commence à `i` (`i` est sur la barre oblique). */
function nomCommande(texte, i) {
    let j = i + 1;
    if (j < texte.length && estLettre(texte.charAt(j))) {
        while (j < texte.length && estLettre(texte.charAt(j)))
            j += 1;
        return { nom: texte.slice(i + 1, j), suite: j };
    }
    return { nom: texte.charAt(j), suite: j + 1 };
}
/**
 * Rend une chaîne de LaTeX LISIBLE. Total : il n'y a pas d'entrée qui le
 * fasse échouer, et jamais de barre oblique inverse ni d'accolade en sortie.
 */
export function replierLatex(texte, enScript = false) {
    let sortie = '';
    let i = 0;
    /** Une matrice ouverte (tranche T41) : `&` y sépare deux cases, `\\` deux lignes. */
    let matrices = 0;
    while (i < texte.length) {
        const c = texte.charAt(i);
        if (c === '&' && matrices > 0) {
            sortie += ' ';
            i += 1;
            continue;
        }
        if (c === '{' || c === '}') {
            // Les accolades de REGROUPEMENT ne se voient pas en LaTeX : elles ne se
            // verront pas ici non plus.
            i += 1;
            continue;
        }
        /* `90^\circ`, sans accolades : le degré, collé à ce qui précède. */
        if (c === '^' && /^\s*\\circ(?![A-Za-z])/.test(texte.slice(i + 1))) {
            sortie += '°';
            i += 1 + /^\s*\\circ/.exec(texte.slice(i + 1))[0].length;
            continue;
        }
        if (c === '^' || c === '_') {
            const arg = argumentScript(texte, i + 1);
            if (arg === null) {
                // Pas un script : le caractère est là pour lui-même (« ___ »).
                sortie += c;
                i += 1;
                continue;
            }
            const interieur = replierLatex(arg.contenu, true);
            /* `90^{\circ}` : le degré est un caractère, pas un exposant à parenthèser. */
            if (c === '^' && interieur.trim() === '°') {
                sortie += '°';
                i = arg.suite;
                continue;
            }
            const unicode = c === '^' ? enExposantUnicode(interieur) : enIndiceUnicode(interieur);
            // `x^{2}` → « x² ». Hors de portée d'Unicode (`x^{n+1}`), on garde le
            // signe du cahier plutôt que d'inventer : « x^(n+1) ».
            sortie += unicode ?? `${c}(${interieur})`;
            i = arg.suite;
            continue;
        }
        if (c !== '\\') {
            sortie += c;
            i += 1;
            continue;
        }
        const { nom, suite } = nomCommande(texte, i);
        i = suite;
        // Les délimiteurs d'expression : ils s'effacent.
        if (nom === '(' || nom === ')' || nom === '[' || nom === ']')
            continue;
        // `\\` (le retour à la ligne de LaTeX) et `\` en fin de chaîne : une
        // espace. Sans ce cas, la barre oblique ressortait telle quelle.
        if (nom === '\\' || nom === '') {
            sortie += matrices > 0 ? ' ; ' : ' ';
            continue;
        }
        /* `\begin{pmatrix} 0 & -1 \\ 1 & 0 \end{pmatrix}` : « (0 -1 ; 1 0) », la
           matrice écrite ligne par ligne, comme `texteDeMath` l'écrit. */
        if (nom === 'begin' || nom === 'end') {
            const env = argument(texte, i);
            const paire = PAIRES_MATRICE[env?.contenu.trim() ?? ''];
            if (env !== null && paire !== undefined) {
                // Un `\\` final, que LaTeX tolère, ne laisse pas de « ; » orphelin.
                if (nom === 'end')
                    sortie = sortie.replace(/\s*;\s*$/, ' ');
                sortie += nom === 'begin' ? paire.charAt(0) : paire.charAt(1);
                matrices += nom === 'begin' ? 1 : -1;
                i = env.suite;
                continue;
            }
        }
        const echappe = ECHAPPES[nom];
        if (echappe !== undefined) {
            sortie += echappe;
            continue;
        }
        if (nom === ' ' || nom === ',' || nom === ';' || nom === ':' || ESPACEMENTS.has(nom)) {
            sortie += ' ';
            continue;
        }
        if (nom === 'frac' || nom === 'dfrac' || nom === 'tfrac') {
            const num = argument(texte, i);
            const den = num === null ? null : argument(texte, num.suite);
            if (num !== null && den !== null) {
                sortie += `${replierLatex(num.contenu)}/${replierLatex(den.contenu)}`;
                i = den.suite;
                continue;
            }
        }
        if (nom === 'sqrt') {
            let j = apresBlancs(texte, i);
            // Le degré `\sqrt[3]{8}` : gardé devant le radical, comme au cahier.
            let degre = '';
            if (texte.charAt(j) === '[') {
                const fin = texte.indexOf(']', j);
                if (fin > 0) {
                    degre = replierLatex(texte.slice(j + 1, fin));
                    j = fin + 1;
                }
            }
            const arg = argument(texte, j);
            if (arg !== null) {
                sortie += `${degre}√(${replierLatex(arg.contenu)})`;
                i = arg.suite;
                continue;
            }
        }
        /* Un ensemble de nombres garde sa double barre : « ℝ », pas « R » — une
           formule refusée pour un autre signe (`\mathbb{N} \subset \mathbb{Z}`)
           ne doit pas changer l'ensemble dont elle parle. */
        if (nom === 'mathbb') {
            const arg = argument(texte, i);
            const ensemble = arg === null ? undefined : ENSEMBLES_DE_NOMBRES[arg.contenu.trim()];
            if (arg !== null && ensemble !== undefined) {
                sortie += ensemble;
                i = arg.suite;
                continue;
            }
        }
        if (TRANSPARENTES.has(nom)) {
            const arg = argument(texte, i);
            if (arg !== null) {
                sortie += replierLatex(arg.contenu);
                i = arg.suite;
            }
            // `\left(` et `\right)` n'ont pas d'argument entre accolades : le
            // délimiteur qui suit est un caractère ordinaire, il passe tel quel.
            continue;
        }
        /* `\circ` a deux emplois, comme dans la grammaire (`latex.ts`) : le degré en
           exposant (`90^\circ`), la composition entre deux fonctions (`f \circ g`). */
        if (nom === 'circ' && !enScript) {
            sortie += '∘';
            continue;
        }
        const signe = SIGNES[nom];
        if (signe !== undefined) {
            sortie += signe;
            continue;
        }
        /* Un nom d'opérateur collé à une commande (`\cos\theta`) : sans l'espace,
           « cosθ » se lirait comme un seul mot. */
        if (NOMS_OPERATEUR.has(nom)) {
            sortie += `${NOM_ECRIT[nom] ?? nom}${texte.charAt(i) === '\\' ? ' ' : ''}`;
            continue;
        }
        // L'inconnue, enfin : la barre oblique tombe, le nom reste. C'est laid, et
        // c'est le but — lisible, et visiblement à corriger en amont.
        sortie += nom;
    }
    return sortie;
}
/**
 * Le repli appliqué à un morceau de PROSE : rendu lisible seulement s'il
 * porte encore de la source. Sans cette garde, une phrase française
 * traverserait le repli pour rien, et `50 % \ 2` (une prose qui n'est pas du
 * LaTeX) serait retouchée.
 *
 * Les espaces sont resserrées APRÈS coup : retirer `\(` `\)` et `\quad` laisse
 * des blancs doubles au milieu d'une phrase, et un blanc devant la
 * ponctuation. La règle suivie est celle du français, la même que
 * `direTexteMath` (`decrire.ts`).
 */
export function replierProse(texte) {
    if (!contientLatexBrut(texte))
        return texte;
    const replie = replierLatex(texte)
        .replace(/[ \t]{2,}/g, ' ')
        .replace(/ +([.,)\]])/g, '$1')
        .replace(/([([]) +/g, '$1');
    // Une prose ne perd jamais ses blancs de bord : ils portent la coupure de
    // ligne entre deux morceaux voisins.
    const gauche = /^\s*/.exec(texte)?.[0] ?? '';
    const droite = /\s*$/.exec(texte)?.[0] ?? '';
    return `${gauche}${replie.trim()}${droite}`;
}
//# sourceMappingURL=repli.js.map